// SPDX-License-Identifier: GPL-3.0-or-later
// Engine Mesh to three.js BufferGeometry.
// Replaces the upstream converter. The upstream code used the removed Geometry and Face3
// classes and a pooled OBJ parser. This code builds a BufferGeometry directly.
//
// Frame: the table frame (x, y, z up) maps to the three.js frame (x, z, y). See coords.ts.
// Winding: the engine meshes use the winding of the reference engine. The swap above flips
// the handedness, so this code sets the winding of each triangle from its vertex normals.
// A front face is the side that the normals point to.

import { type BufferAttribute, BufferGeometry, Float32BufferAttribute } from "three";
import type { Mesh } from "../engine/mesh.ts";

/** True when the index points to a vertex. */
function inRange(i: number, count: number): boolean {
  return i >= 0 && i < count;
}

/** The triangle index of a mesh. A triangle with a missing vertex is dropped. */
function windIndex(
  indices: readonly number[],
  count: number,
  positions: Float32Array,
  normals: Float32Array,
): number[] {
  const index: number[] = [];
  for (let t = 0; t + 2 < indices.length; t += 3) {
    const a = indices[t] as number;
    let b = indices[t + 2] as number;
    let c = indices[t + 1] as number;
    if (!(inRange(a, count) && inRange(b, count) && inRange(c, count))) continue;
    if (windingDot(positions, normals, a, b, c) < 0) {
      const swap = b;
      b = c;
      c = swap;
    }
    index.push(a, b, c);
  }
  return index;
}

/**
 * Convert one engine mesh. A triangle that points to a missing vertex is dropped.
 * The geometry has position, normal and uv attributes and an index.
 */
export function meshToGeometry(mesh: Mesh): BufferGeometry {
  const count = mesh.vertices.length;
  const positions = new Float32Array(count * 3);
  const normals = new Float32Array(count * 3);
  const uvs = new Float32Array(count * 2);
  for (let i = 0; i < count; i++) {
    const v = mesh.vertices[i];
    if (!v) continue;
    positions[i * 3] = v.x;
    positions[i * 3 + 1] = v.z;
    positions[i * 3 + 2] = v.y;
    normals[i * 3] = v.nx;
    normals[i * 3 + 1] = v.nz;
    normals[i * 3 + 2] = v.ny;
    uvs[i * 2] = v.tu;
    uvs[i * 2 + 1] = 1 - v.tv;
  }
  const index = windIndex(mesh.indices, count, positions, normals);

  const geometry = new BufferGeometry();
  geometry.name = mesh.name;
  geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geometry.setAttribute("normal", new Float32BufferAttribute(normals, 3));
  geometry.setAttribute("uv", new Float32BufferAttribute(uvs, 2));
  geometry.setIndex(index);
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Dot product of the face normal from the winding (counter-clockwise a, b, c) and the sum of
 * the three vertex normals. A negative value means that the winding faces away from the normals.
 */
function windingDot(p: Float32Array, n: Float32Array, a: number, b: number, c: number): number {
  const ax = p[a * 3] as number;
  const ay = p[a * 3 + 1] as number;
  const az = p[a * 3 + 2] as number;
  const e1x = (p[b * 3] as number) - ax;
  const e1y = (p[b * 3 + 1] as number) - ay;
  const e1z = (p[b * 3 + 2] as number) - az;
  const e2x = (p[c * 3] as number) - ax;
  const e2y = (p[c * 3 + 1] as number) - ay;
  const e2z = (p[c * 3 + 2] as number) - az;
  const fx = e1y * e2z - e1z * e2y;
  const fy = e1z * e2x - e1x * e2z;
  const fz = e1x * e2y - e1y * e2x;
  const nx = (n[a * 3] as number) + (n[b * 3] as number) + (n[c * 3] as number);
  const ny = (n[a * 3 + 1] as number) + (n[b * 3 + 1] as number) + (n[c * 3 + 1] as number);
  const nz = (n[a * 3 + 2] as number) + (n[b * 3 + 2] as number) + (n[c * 3 + 2] as number);
  return fx * nx + fy * ny + fz * nz;
}

/** The place of the first value that is not finite, or -1 when all values are finite. */
function firstNonFinite(array: ArrayLike<number>): number {
  for (let i = 0; i < array.length; i++) {
    if (!Number.isFinite(array[i])) return i;
  }
  return -1;
}

/** Problems of an index: a bad count or a value that points past the last vertex. */
function indexProblems(index: BufferAttribute, vertexCount: number): string[] {
  const problems: string[] = [];
  if (index.count % 3 !== 0) problems.push("index count is not a multiple of 3");
  for (let i = 0; i < index.count; i++) {
    if (!inRange(index.getX(i), vertexCount)) {
      problems.push(`index ${i} is out of range`);
      break;
    }
  }
  return problems;
}

/**
 * Check a geometry. Every position is finite and every index points to a vertex.
 * Returns a list of problems. An empty list means that the geometry is valid.
 * Set `isLine` for the geometry of a line: it needs no multiple of 3 positions.
 */
export function validateGeometry(geometry: BufferGeometry, isLine = false): string[] {
  const problems: string[] = [];
  const position = geometry.getAttribute("position");
  if (!position || position.count === 0) {
    problems.push("no positions");
    return problems;
  }
  const badPosition = firstNonFinite(position.array);
  if (badPosition >= 0) problems.push(`position ${badPosition} is not finite`);
  const normal = geometry.getAttribute("normal");
  const badNormal = normal ? firstNonFinite(normal.array) : -1;
  if (badNormal >= 0) problems.push(`normal ${badNormal} is not finite`);
  const index = geometry.getIndex();
  if (index) {
    problems.push(...indexProblems(index, position.count));
  } else if (!isLine && position.count % 3 !== 0) {
    problems.push("position count is not a multiple of 3");
  }
  return problems;
}
